import { css } from "styled-system/css";
import { ed, svgText, niceMax } from "./tokens";
import { useTooltip } from "./useTooltip";

export interface ColumnPoint {
  key: string;
  /** The series the figure is about, drawn at the base. */
  value: number;
  /** Optional remainder stacked on top in gray. */
  stack?: number;
  /** Axis label; defaults to `formatKey(key)`. */
  label?: string;
  /** Longer name for the tooltip, e.g. a full date; defaults to the label. */
  title?: string;
  /** Short text written above the column, e.g. a share. */
  note?: string;
  /** Draw this column in gray, e.g. a prior period beside the current one. */
  muted?: boolean;
}

export interface ColumnsProps {
  points: ColumnPoint[];
  ariaLabel: string;
  /** Unit after the value in the tooltip, e.g. "commits". */
  unit?: string;
  /** Legend name for the base series. Shown when `stackLabel` is set. */
  valueLabel?: string;
  /** Legend and tooltip name for the stacked remainder. */
  stackLabel?: string;
  /** Where the legend goes: a key line under the figure, or inside it. */
  legend?: "below" | "inline";
  /**
   * Color for the base series: ink by default, emph when it's the point,
   * strong gray when nothing in it is.
   */
  tone?: "ink" | "emph" | "strong";
  /** Spend the emphasis color on the tallest column (the one the title names). */
  emphasizeMax?: boolean;
  /** Draw each column's value above it (for short series like weekdays). */
  valueLabels?: boolean;
  /** Tooltip and value-label format. */
  format?: (v: number) => string;
  /** Y axis label format. Defaults to `format`, with one decimal off the integers. */
  tickFormat?: (v: number) => string;
  /** Axis label from the point key, when the point has no `label`. */
  formatKey?: (key: string) => string;
  width?: number;
  height?: number;
  /** Top and left margins. Left defaults to the width of the widest tick label. */
  margin?: { t?: number; l?: number };
  /** Steps between zero and the top tick; 1 rounds the top up more loosely. */
  steps?: number;
  /** Most x axis labels to draw; the rest are skipped evenly. */
  maxLabels?: number;
  /** Column width as a share of its slot, and the widest a column may get. */
  barRatio?: number;
  maxBarWidth?: number;
}

const defaultFormat = (v: number) => Math.round(v).toLocaleString("en-US");

/**
 * One column per period (day, week or month), labeled directly, with an
 * optional gray remainder stacked on top, an optional emphasized peak and
 * optional value labels. Gridlines at zero, half and the top.
 */
export function Columns({
  points,
  ariaLabel,
  unit,
  valueLabel,
  stackLabel,
  legend = "below",
  tone = "ink",
  emphasizeMax = false,
  valueLabels = false,
  format = defaultFormat,
  tickFormat,
  formatKey = (k) => k,
  width = 720,
  height = 150,
  margin,
  steps = 1,
  maxLabels = 10,
  barRatio = 0.7,
  maxBarWidth = Infinity,
}: ColumnsProps) {
  const { bind, node } = useTooltip();
  const W = width;
  const H = height;
  const totals = points.map((p) => p.value + (p.stack ?? 0));
  const top = niceMax(Math.max(1, ...totals), steps);
  const tick =
    tickFormat ?? ((t: number) => (Number.isInteger(t) ? format(t) : t.toFixed(1)));
  const labelChars = Math.max(...[0, top / 2, top].map((t) => tick(t).length));
  const M = {
    t: margin?.t ?? (valueLabels ? 18 : 10),
    r: 8,
    b: 22,
    l: margin?.l ?? Math.max(28, labelChars * 6.5 + 10),
  };
  const iw = W - M.l - M.r;
  const ih = H - M.t - M.b;
  const step = points.length > 0 ? iw / points.length : iw;
  const bw = Math.max(2, Math.min(maxBarWidth, step * barRatio));
  const y = (v: number) => M.t + ih * (1 - v / top);
  const every = Math.max(1, Math.ceil(points.length / maxLabels));
  const peak = Math.max(0, ...points.map((p) => p.value));
  const peakIdx = points.findIndex((p) => p.value === peak && peak > 0);
  const base = tone === "emph" ? ed.emph : tone === "strong" ? ed.markStrong : ed.ink;
  const showNotes = points.length <= 16;
  const valueName = unit ?? valueLabel?.toLowerCase() ?? "";

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }} role="img" aria-label={ariaLabel}>
        {[0, top / 2, top].map((t) => (
          <g key={t}>
            <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} stroke={t === 0 ? ed.ink3 : ed.grid} />
            <text x={M.l - 6} y={y(t) + 3} textAnchor="end" style={svgText.axis}>
              {tick(t)}
            </text>
          </g>
        ))}
        {points.map((p, i) => {
          const cx = M.l + step * i + step / 2;
          const sec = p.stack ?? 0;
          const isPeak = emphasizeMax && i === peakIdx;
          const fill = p.muted ? ed.mark : emphasizeMax ? (isPeak ? ed.emph : ed.mark) : base;
          const label = p.label ?? formatKey(p.key);
          return (
            <g
              key={p.key}
              {...bind(
                <>
                  <b>{p.title ?? label}</b>
                  <span className="num">{format(p.value)}</span> {valueName}
                  {stackLabel && (
                    <>
                      , <span className="num">{format(sec)}</span> {stackLabel.toLowerCase()}
                    </>
                  )}
                  {p.note && (
                    <>
                      {" "}
                      (<span className="num">{p.note}</span>)
                    </>
                  )}
                </>,
              )}
            >
              <rect x={cx - step / 2} y={M.t} width={step} height={ih} fill="transparent" />
              {p.value > 0 && (
                <rect
                  x={cx - bw / 2}
                  y={y(p.value)}
                  width={bw}
                  height={Math.max(0, y(0) - y(p.value))}
                  fill={fill}
                />
              )}
              {sec > 0 && (
                <rect
                  x={cx - bw / 2}
                  y={y(p.value + sec)}
                  width={bw}
                  height={Math.max(0, y(p.value) - y(p.value + sec))}
                  fill={ed.mark}
                />
              )}
              {valueLabels && p.value > 0 && (
                <text
                  x={cx}
                  y={y(p.value + sec) - 4}
                  textAnchor="middle"
                  style={{
                    ...svgText.axis,
                    fill: isPeak ? ed.ink : ed.ink2,
                    fontWeight: isPeak ? 600 : 400,
                  }}
                >
                  {format(p.value)}
                </text>
              )}
              {showNotes && p.note && (
                <text x={cx} y={y(p.value + sec) - 4} textAnchor="middle" style={svgText.axis}>
                  {p.note}
                </text>
              )}
              {i % every === 0 && (
                <text x={cx} y={H - 6} textAnchor="middle" style={svgText.axis}>
                  {label}
                </text>
              )}
            </g>
          );
        })}
        {stackLabel && legend === "inline" && (
          <>
            <text x={M.l} y={12} style={{ ...svgText.label, fill: ed.ink }}>
              ■ {valueLabel}
            </text>
            <text x={M.l + 90} y={12} style={{ ...svgText.label, fill: ed.ink3 }}>
              ■ {stackLabel}
            </text>
          </>
        )}
      </svg>
      {stackLabel && legend === "below" && (
        <div className={keyLine}>
          <span>
            <i style={{ background: base }} />
            {valueLabel}
          </span>
          <span>
            <i style={{ background: ed.mark }} />
            {stackLabel}
          </span>
        </div>
      )}
      {node}
    </>
  );
}

const keyLine = css({
  display: "flex",
  flexWrap: "wrap",
  gap: "4px 14px",
  mt: "4px",
  fontFamily: "sans",
  fontSize: "11.5px",
  color: "ed.ink2",
  "& i": {
    display: "inline-block",
    width: "9px",
    height: "9px",
    mr: "5px",
    verticalAlign: "-1px",
  },
});
